// src/components/practices/MagnificentOutcomes.js
// Magnificent Outcomes — think of something coming up, then imagine yourself afterward,
// grateful it went beautifully. Two fixed questions (topic, then the gratitude response), only
// the second of which is a gratitude statement — so the thank-you-tap gate only appears on step
// two, same shape as Way Out of Negativity will use later, distinct from Magical To-Do List
// where every step is its own gratitude. Reuses the same shared primitives as Magic Rock and
// Magical To-Do List; no changes to the Practices library/nav architecture.
import React, { useEffect, useRef, useState } from 'react'
import { supabase } from '../../supabaseClient'
import PageHeader from '../ui/PageHeader'
import PrimaryButton from '../ui/PrimaryButton'
import PracticeStepRing from './shared/PracticeStepRing'
import PracticeThankYouTap from './shared/PracticeThankYouTap'
import PracticeComplete from './shared/PracticeComplete'
import './shared/PracticeHeading.css'
import './MagnificentOutcomes.css'

const REREAD_MS = 2000
const TOTAL_STEPS = 2

export default function MagnificentOutcomes({ practice, session, city, onComplete, onBack, augustTheme, onAugustThemeChange }) {
  // Starts on 'step1' directly — the detail screen (AugustPracticeDetail) already shows this
  // Practice's tagline + description before "Begin Practice" is tapped; a separate in-flow intro
  // phase repeating that same copy was an extra tap with no new information, not a real step
  // (never counted toward TOTAL_STEPS).
  const [phase, setPhase] = useState('step1') // 'step1' | 'step2' | 'complete'
  const [topic, setTopic] = useState('')
  const [gratitude, setGratitude] = useState('')
  const [thanks, setThanks] = useState(0)
  const [reread, setReread] = useState(false)
  const [saving, setSaving] = useState(false)
  const rereadTimer = useRef(null)

  useEffect(() => () => clearTimeout(rereadTimer.current), [])

  const topicTyped = topic.trim().length > 2
  const gratitudeTyped = gratitude.trim().length > 2
  const isReady = gratitudeTyped && thanks >= 3
  const eyebrow = `Day ${practice.unlockDay} · ${practice.name}`

  const tapThanks = () => {
    if (thanks >= 3) return
    const next = thanks + 1
    setThanks(next)
    if (next < 3) {
      setReread(true)
      clearTimeout(rereadTimer.current)
      rereadTimer.current = setTimeout(() => setReread(false), REREAD_MS)
    }
  }

  const handleFinish = async () => {
    if (!isReady || saving) return
    setSaving(true)
    if (session) {
      await supabase.from('practice_completions').insert({
        user_id: session.user.id,
        city: city || 'august',
        practice_id: practice.id,
        payload: { topic, gratitude },
      })
    }
    setSaving(false)
    setPhase('complete')
  }

  if (phase === 'step1') {
    return (
      <div className="practice-screen" data-city="august">
        <PageHeader
          eyebrow={eyebrow} title="Name the Outcome"
          descriptor={`Step 1 of ${TOTAL_STEPS}`}
          showBack onBack={onBack}
          augustTheme={augustTheme} onAugustThemeChange={onAugustThemeChange}
        />
        <div className="practice-screen-body">
          <PracticeStepRing current={1} total={TOTAL_STEPS} />

          <p className="practice-question">What would you like a magnificent outcome for?</p>
          <div className="stops-input-row">
            <textarea
              className="stops-input"
              placeholder="A situation, event, or decision…"
              value={topic}
              onChange={e => setTopic(e.target.value)}
            />
          </div>

          <PrimaryButton onClick={topicTyped ? () => setPhase('step2') : undefined} disabled={!topicTyped}>
            <span>Next</span>
            <span>→</span>
          </PrimaryButton>
        </div>
      </div>
    )
  }

  if (phase === 'step2') {
    return (
      <div className="practice-screen" data-city="august">
        <PageHeader
          eyebrow={eyebrow} title="Imagine It's Already Happened"
          descriptor={`Step 2 of ${TOTAL_STEPS}`}
          showBack onBack={() => setPhase('step1')}
          augustTheme={augustTheme} onAugustThemeChange={onAugustThemeChange}
        />
        <div className="practice-screen-body">
          <PracticeStepRing current={2} total={TOTAL_STEPS} />

          <h3 className="practice-step-label">{topic}</h3>
          <p className="practice-question">Imagine it's already happened. How would you feel if it went even better than you hoped?</p>

          <div className="stops-input-row">
            <textarea
              className="stops-input"
              placeholder="I'm so grateful that…"
              value={gratitude}
              onChange={e => setGratitude(e.target.value)}
            />
          </div>

          <PracticeThankYouTap
            count={thanks}
            reread={reread}
            canTap={gratitudeTyped}
            onTap={tapThanks}
            writtenText={gratitude}
          />

          <PrimaryButton onClick={isReady ? handleFinish : undefined} disabled={!isReady || saving}>
            <span>Set This Outcome</span>
            <span>→</span>
          </PrimaryButton>
        </div>
      </div>
    )
  }

  // phase === 'complete'
  return (
    <PracticeComplete
      headline="Magnificent Outcome Set"
      subline="Now let it unfold."
      onDone={onComplete}
    />
  )
}
